<template>
  <v-sheet class = "timeline-pane rounded-sm">

    <!-- Controls -->
    <div
      v-if  = "canPlay"
      class = "timeline-controls py-1 px-4 .rounded-t-sm"
    >

      <!-- Time Source Selector -->
      <Timecode></Timecode>

      <!-- Current Time -->
      <div class = "current-time text-h5"
           @contextmenu="onTimeClick"
      >
        <timecode-input
          class       = "input"
          v-if        = "timeDisplaySource && timeDisplaySource.type === 'timecode' && timeDisplaySource.name != nptName()"
          v-model     = "timecode"
          showFramerate
          :background = "'transparent'"
        >
        </timecode-input>

        <time-input
          class       = "input"
          v-if        = "timeDisplaySource && timeDisplaySource.type === 'seconds'"
          v-model     = "time"
          :background = "'transparent'"
        >
        </time-input>

        <time-input
          class       = "input"
          v-if        = "timeDisplaySource && timeDisplaySource.type === 'timecode' && timeDisplaySource.name == nptName()"
          v-model     = "npt"
          :background = "'transparent'"
        >
        </time-input>

        <frame-input
          class       = "frame input"
          v-if        = "timeDisplaySource && timeDisplaySource.type === 'frames'"
          v-model     = "frame"
          :background = "'transparent'"
        >
        </frame-input>

      </div>

      <div class = "zoom-controls">

        <!-- Zoom Out -->
        <VideoControlButton
          tooltip     = "Zoom out"
          color       = "default"
          hotkey      = "-"
          @click      = "zoomOut()"
        >
          <v-icon>mdi-magnify-minus</v-icon>
        </VideoControlButton>

        <!-- Zoom In -->
        <VideoControlButton
          tooltip     = "Zoom in"
          color       = "default"
          hotkey      = "+"
          @click      = "zoomIn()"
        >
          <v-icon>mdi-magnify-plus</v-icon>
        </VideoControlButton>

        <!-- View All -->
        <VideoControlButton
          v-if        = "ui.waveformZoomLevel !== 1"
          tooltip     = "View Entire Timeline"
          @click      = "viewEntireTimeline()"
        >
          <v-icon>mdi-magnify-expand</v-icon>
        </VideoControlButton>

        <!-- View Default -->
        <VideoControlButton
          v-else
          tooltip     = "View At Default Zoom Level"
          @click      = "zoomAtDefault();"
        >
          <v-icon>mdi-magnify</v-icon>
        </VideoControlButton>

        <WaveformMarks></WaveformMarks>

      </div>

    </div>

    <Timeline
      v-if  = "canPlay"
      ref   = "timeline"
      class = "timeline"
    >
    </Timeline>

  </v-sheet>
</template>

<script lang = "js">

import Timeline from "@/components/panes/components/timeline/Timeline";
import Timecode from "@/components/panes/components/timeline/Timecode";
import {mapState} from "vuex";
import WaveformMarks from "@/components/panes/components/timeline/WaveformElements";
import {getTimecodeTime, getFrameDuration} from "@/utils/timecode-utilities";
import {JUMP_TO_FRAME, JUMP_TO_TIME} from "@/utils/action-types";
import VideoControlButton from "@/components/panes/components/buttons/VideoControlButton";
import { nptToTime } from '@/store/utilities/timecodes.utils';
import { NPT_TIMECODE_SOURCE } from '@/utils/constants';
import {LEVELS, sendEphemeralSnack} from "@/store/utilities/snacks.utils";
import {splitTime} from "@/utils/time-utilities";

export default {

  // #region Properties

  name:       'TimelinePane',
  components: {WaveformMarks, Timecode, Timeline, VideoControlButton},

  // #endregion Properties

  // #region Operations

  computed: {
    ...mapState(['video', 'videoCurrentTime', 'videoCurrentTimecode', 'timeDisplaySource', 'fps', 'ui']),
    canPlay() {
      return this.video.canPlay;
    },
    timecode: {
      get: function () {
        return this.video.currentTimecode;
      },
      set: function (timecode) {
        const timecodeAsTime = getTimecodeTime(timecode, this.$store.state.video.element, this.timeDisplaySource);
        const frameDuration = getFrameDuration(timecode);
        const timeToJumpTo = timecodeAsTime + (frameDuration / 2);

        this.$store.dispatch(JUMP_TO_TIME, timeToJumpTo);
      }
    },

    npt: {
      get: function () {
        return nptToTime(this.video.currentTimecode);
      },
      set: function (time) {
        this.$store.dispatch(JUMP_TO_TIME, time);
      }
    },

    time: {
      get() {
        return this.videoCurrentTime;
      },
      set(time) {
        this.$store.dispatch(JUMP_TO_TIME, time);
      }
    },

    frame: {
      get() {
        return this.video.currentFrame.toString();
      },
      set(frame) {
        this.$store.dispatch(JUMP_TO_FRAME, Number(frame));
      }
    }
  },


  methods: {

    zoomOut() {
      this.$refs.timeline.zoomOut();
    },

    zoomIn() {
      this.$refs.timeline.zoomIn();
    },

    viewEntireTimeline() {
      this.$refs.timeline.zoomAllTheWayOut();
    },

    zoomAtDefault() {
      this.$refs.timeline.zoomAtDefault();
    },

    nptName() {
      return NPT_TIMECODE_SOURCE.name;
    },

    onTimeClick(event){
      event.preventDefault();
      let timeSourceDisplay;
      let message;
      if (this.timeDisplaySource && this.timeDisplaySource.type === 'timecode' && this.timeDisplaySource.name !== this.nptName()){
        timeSourceDisplay = this.timecode
        message = `Timecode ${timeSourceDisplay} copied to clipboard.`
      }
      else if (this.timeDisplaySource && this.timeDisplaySource.type === 'timecode' && this.timeDisplaySource.name === this.nptName()){
        let npt =  nptToTime(this.video.currentTimecode);
        let time = splitTime(npt);
        timeSourceDisplay = `${time[0]}:${time[1]}:${time[2]}.${time[3]}`;
        message = `Normal play time ${timeSourceDisplay} copied to clipboard.`
      }
      else if (this.timeDisplaySource && this.timeDisplaySource.type === 'seconds'){
        let time = splitTime(this.videoCurrentTime);
        timeSourceDisplay = `${time[0]}:${time[1]}:${time[2]}.${time[3]}`;
        message = `Time ${timeSourceDisplay} copied to clipboard.`
      }
      else if (this.timeDisplaySource && this.timeDisplaySource.type === 'frames'){
        timeSourceDisplay = this.frame;
        message = `Frame number ${timeSourceDisplay} copied to clipboard.`
      }
      navigator.clipboard.writeText(timeSourceDisplay)
        .then(() => {
          sendEphemeralSnack(this.$store, LEVELS.INFO, message);
        })
        .catch(() => {
          sendEphemeralSnack(this.$store, LEVELS.ERROR, 'Failed to copy timeSourceDisplay');
        });
    }

  }

  // #endregion Properties

};
</script>

<style scoped lang = "scss">
.timeline-pane {
  display        : flex;
  flex-direction : column;
  overflow: hidden;

  .timeline-controls {
    display               : grid;
    grid-template-columns : 1fr 1fr 1fr;
    align-items           : center;
    background-color      : rgba(0, 0, 0, 0.3);
    justify-content       : center;

    .current-time {
      flex            : 1;
      display         : flex;
      justify-content : center;

      .input {
        width : min-content;

        &.frame {
          min-width:    200px;
        }
      }
    }

    .zoom-controls {
      text-align : right;
    }
  }

  .timeline {
    flex : 1;
  }
}

</style>
